import { css } from '@emotion/react';
import { Collapse, Icon, Select } from 'antd';
import Router, { withRouter } from 'next/router';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  getFieldOptions,
  loadResults,
  resetResults,
  resetSearchCriteria,
  setCurrentPage,
  updateSearchCriteria,
} from '@/src/amicus/redux/amicusReducer';
import parseURL from '@/src/legalCases/utils';
import Button from '@/src/shared/components/Button';
import Form from '@/src/shared/components/Form';
import Heading from '@/src/shared/components/Heading';
import { form as formStyle, space } from '@/styles';
import formatURL from '@/utils/formatURL';
import { escapeGTMParam, trackGTMEvent } from '@/utils/utils';

import style from './style';

const filterColorOverride = css`
  background-color: #fff
`

const { m } = space;

const { Option } = Select;

const FIELD_NAMES = {
  CASE_NAME: 'CaseNames',
  STATE: 'States',
  ISSUE: 'Issues',
  STATUS: 'Statuses',
};
const filterCollapseProps = {
  bordered: false,
  expandIconPosition: 'right',
  expandIcon: ({ isActive }) => <Icon type={isActive ? 'minus' : 'plus'} />,
};

const SearchFilters = props => {
  const { form, router, handleFiltersCollapseChange } = props;
  const dispatch = useDispatch();
  const {
    fieldOptionsData: { options, isFetchingOptions },
    searchCriteria,
    searchData: { SortField, SortOrder },
  } = useSelector(state => state.amicus);
  const { statuses, states, caseNames, issues } = options;
  const { query } = router;
  const caseOptions = caseNames.filter(c => c.CaseTypeId === 1);
  const handleSearch = async e => {
    if (e && 'preventDefault' in e) {
      e.preventDefault();
    }
    await form.validateFieldsAndScroll(async (err, values) => {
      const { States, Issues, Statuses, CaseNames } = values;

      if (err) return;

      dispatch(setCurrentPage(1));
      const payload = {
        searchOptions: {
          AmicusFieldOptions: {
            CaseTypes: [
              {
                CaseTypeCode: '1',
              },
            ],
            CaseNames: CaseNames
              ? CaseNames.map(s => {
                return { Value: s };
              })
              : [],
            Statuses: Statuses
              ? Statuses.map(s => {
                return { StatusCode: s };
              })
              : [],
            Issues: Issues
              ? Issues.map(i => {
                return { IssueId: i };
              })
              : [],
            States: States
              ? States.map(s => {
                return { StateCode: s };
              })
              : [],
            SortOrder,
            SortField,
            PageSize: 10,
            Page: 1,
          },
        },
      };

      const queryObj = {
        statuses: Statuses.join(','),
        issues: Issues.join(','),
        states: States.join(','),
        cases: CaseNames.join(','),
      };

      const selectedCaseNames = options.caseNames
        .filter(c => CaseNames.includes(c.Value))
        .map(c => escapeGTMParam(c.Text));
      const selectedIssues = options.issues
        .filter(i => Issues.includes(i.IssueId))
        .map(i => escapeGTMParam(i.IssueName));

      trackGTMEvent({
        event: 'AmicusSearch',
        caseName: selectedCaseNames?.length > 0 ? selectedCaseNames.join(', ') : 'All',
        state: States?.length > 0 ? States.join(', ') : 'All',
        issue: selectedIssues?.length > 0 ? selectedIssues.join(', ') : 'All',
        status:
          Statuses?.length > 0
            ? options.statuses
              .filter(s => Statuses.includes(s.StatusCode))
              .map(s => escapeGTMParam(s.StatusDescription))
              .join(', ')
            : 'All',
      });

      const formattedURL = formatURL('/advocacy/legal-judicial-advocacy/amicus', queryObj);
      Router.push(formattedURL, formattedURL, { shallow: true });
      dispatch(loadResults(payload));
    });
  };

  const handleResetAll = () => {
    const formattedURL = '/advocacy/legal-judicial-advocacy/amicus';
    Router.push(formattedURL, formattedURL, { shallow: true });
    dispatch(resetResults());
    form.setFieldsValue({
      CaseNames: [],
      States: [],
      Issues: [],
      Statuses: [],
    });
    dispatch(resetSearchCriteria());
  };

  useEffect(() => {
    dispatch(getFieldOptions());
  }, []);

  const handleCriteriaChange = (name, value) => {
    dispatch(updateSearchCriteria({ name, value }));
  };
  useEffect(() => {
    const searchPayload = parseURL(options, router, handleCriteriaChange);
    const payload = {
      searchOptions: {
        AmicusFieldOptions: {
          CaseTypes: [
            {
              CaseTypeCode: '1',
            },
          ],
          ...searchPayload,
          SortOrder,
          SortField,
        },
      },
    };
    dispatch(loadResults(payload));
  }, [query]);

  const renderFilterSection = opts => {
    const { label, name } = opts;
    return (
      <Collapse {...filterCollapseProps} css={filterColorOverride}>
        <Collapse.Panel
          header={
            <Heading type={4} weight="bolder" theme="primaryGray">
              {label}
            </Heading>
          }
        >
          <Form.Item>
            {form.getFieldDecorator(name, {
              initialValue: searchCriteria[name],
            })(
              <Select
                css={formStyle.inputGray}
                loading={isFetchingOptions}
                mode="multiple"
                size="large"
                allowClear
                optionFilterProp="children"
                placeholder="All"
                onChange={(v, s) => {
                  handleCriteriaChange(name, v);
                }}
              >
                {opts.options}
              </Select>
            )}
          </Form.Item>
        </Collapse.Panel>
      </Collapse>
    );
  };

  return (
    <div css={style.filters}>
      <Collapse
        defaultActiveKey={['1']}
        onChange={handleFiltersCollapseChange}
        {...filterCollapseProps}
        css={filterColorOverride}
      >
        <Collapse.Panel
          isActive
          key="1"
          header={
            <Heading type={3} weight="bolder" theme="primaryGray">
              Search Filters
            </Heading>
          }
        >
          <Form onSubmit={handleSearch}>
            {renderFilterSection({
              label: 'Case Name',
              name: FIELD_NAMES.CASE_NAME,
              options: caseOptions.map(c => (
                <Option key={`${c.Value}-case`} value={c.Value}>
                  {c.Text}
                </Option>
              )),
            })}
            {renderFilterSection({
              label: 'State',
              name: FIELD_NAMES.STATE,
              options: states.map(s => (
                <Option key={`${s.StateCode}-state`} value={s.StateCode}>
                  {s.StateDescription}
                </Option>
              )),
            })}
            {renderFilterSection({
              label: 'Issue',
              name: FIELD_NAMES.ISSUE,
              options: issues.map(i => (
                <Option key={`${i.IssueId}-issue`} value={i.IssueId}>
                  {i.IssueName}
                </Option>
              )),
            })}
            {renderFilterSection({
              label: 'Status',
              name: FIELD_NAMES.STATUS,
              options: statuses.map(s => (
                <Option key={`${s.StatusCode}-status`} value={s.StatusCode}>
                  {s.StatusDescription}
                </Option>
              )),
            })}
          </Form>
        </Collapse.Panel>
      </Collapse>
      <div css={[m.t16]}>
        <Button
          type="button"
          size="small"
          css={[m.r16, m.b16]}
          htmlType="submit"
          onClick={handleSearch}
        >
          Search
        </Button>
        <Button size="small" theme="gray" onClick={handleResetAll}>
          <Icon type="sync" /> Reset All
        </Button>
      </div>
    </div>
  );
};

SearchFilters.propTypes = {};

export default withRouter(SearchFilters);
